import React, { useEffect, useState, useRef } from "react";
import { Flex, Box } from "rebass/styled-components";
import styled from "styled-components";
import Tilt from "react-parallax-tilt";
import { MutedIcon, UnMutedIcon } from "../../../../../../Components/Icons";

/*  Our LOCAL component imports */
import { CreatorCard } from "../../../../../../Components/CreatorCard";

/*  Our GLOBAL component imports */
import { ReactComponent as EnableAll } from "../../../../../../../assets/images/studio/enable-all-info.svg";

import {
  Title3,
  Headline,
  Subheader,
  Large3,
} from "../../../../../../Components/Text";

import theme from "../../../../../../../theme/themeThree";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { StudioSession } from "../../../../../../State/studioSession";
import { EStudioTabView } from "../../../../../../@types";

import { ToggleAllButton } from "../../../ToggleAllButton";
import { Controls } from "../../../../../../Components/CreatorCard/Components/Controls";
import { TieredCard } from "../../../../../../Components/Tooltip/Components/TieredCard";
import { hasScope } from "../../../../../../Utilities/Studio";
import { useUser } from "../../../../../../Hooks/useUser";
import { useShouldShowSubscriptionOverhaul } from "../../../../../../Hooks/useSubscriptionOverhaul";

// not transitioned.

const HomePage = ({
  handleToggle,
  handleCardClick,
  setModal,
  userGroup,
  muted,
  setMuted,
  setCardPlaying,
  cardPlaying,
  onQuery,
  homepageData,
  remixedCards,
  handleToggleAll,
}: any) => {
  // With this we can preload the component and not render anything on no data. Fail Safe
  if (!homepageData || !Array.isArray(homepageData) || !homepageData.length) {
    return (
      <>
        <Empty py={8} flexDirection="column">
          <Title3 my={3}>Better Homepage Coverage Needed!!!</Title3>
        </Empty>
        <NoCardsFound />
      </>
    );
  }
  // Validate we have an array of creator cards:
  if (!homepageData.some((x) => Array.isArray(x.entries))) {
    // if not dont try to render a list
    return (
      <>
        <Empty py={8} flexDirection="column">
          <Title3 my={3}>Better Homepage Coverage Needed!!!</Title3>
        </Empty>
        <NoCardsFound />
      </>
    );
  }

  return (
    <>
      {homepageData.map(
        (
          x: { indentifier: string; label: string; entries: any[] },
          index: number,
        ) => {
          return (
            <React.Fragment key={x.label + index}>
              <Flex justifyContent={"space-between"} alignItems={"center"}>
                <StyledLarge3 my={4}>{x.label}</StyledLarge3>
                <ToggleAllButton
                  handleToggleAll={handleToggleAll}
                  cards={x.entries}
                />
              </Flex>

              <GenerateCardGridFromData
                cards={x.entries}
                handleToggle={handleToggle}
                handleCardClick={handleCardClick}
                userGroup={userGroup}
                muted={muted}
                setMuted={setMuted}
                setCardPlaying={setCardPlaying}
                cardPlaying={cardPlaying}
                setModal={setModal}
                onQuery={onQuery}
                isHomepage={true}
                remixedCards={remixedCards}
              />
            </React.Fragment>
          );
        },
      )}
    </>
  );
};

const Collections = ({
  handleToggle,
  handleCardClick,
  setModal,
  userGroup,
  muted,
  setMuted,
  setCardPlaying,
  cardPlaying,
  onQuery,
}: any) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "studio",
  });
  const { userActivated } = useRecoilValue(StudioSession);

  // With this we can preload the component and not render anything on no data. Fail Safe
  if (
    !userActivated ||
    !Array.isArray(userActivated) ||
    !userActivated.length
  ) {
    return (
      <>
        <NoCardsFound />
      </>
    );
  }

  return (
    <>
      <Flex flexDirection={"column"}>
        <StyledLarge3 mb={4}>{t("enabledCards")}</StyledLarge3>
        <Headline minWidth={"70px"} mr={5}>
          {userActivated.length} {t("cards")}
        </Headline>
      </Flex>
      <Box py={5}>
        <GenerateCardGridFromData
          cards={userActivated}
          handleToggle={handleToggle}
          handleCardClick={handleCardClick}
          userGroup={userGroup}
          muted={muted}
          setMuted={setMuted}
          setCardPlaying={setCardPlaying}
          cardPlaying={cardPlaying}
          setModal={setModal}
          onQuery={onQuery}
        />
      </Box>
    </>
  );
};

const NoCardsFound = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "studio",
  });
  return (
    <>
      <Empty py={8} flexDirection="column">
        <StyledText
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <StyledTitle3 mb={3}>{t("emptyHeader")} </StyledTitle3>
          <EnableAll />
          <StyledSubheader mt={3}>{t("emptyBody")}</StyledSubheader>
        </StyledText>
      </Empty>
    </>
  );
};

export function CardGrid({
  cards,
  handleToggle,
  handleCardClick,
  setModal,
  userGroup,
  homepage,
  homepageData,
  onQuery,
  remixedCards,
  muted,
  setMuted,
  view,
  handleToggleAll,
  category,
}: any) {
  const [cardPlaying, setCardPlaying] = useState();

  useEffect(() => {}, [cards, homepageData]);
  if (view === EStudioTabView.Collections)
    return (
      <Collections
        handleToggle={handleToggle}
        handleCardClick={handleCardClick}
        userGroup={userGroup}
        muted={muted}
        setMuted={setMuted}
        setCardPlaying={setCardPlaying}
        cardPlaying={cardPlaying}
        setModal={setModal}
        onQuery={onQuery}
      />
    );
  if (homepage) {
    return (
      <Box>
        <HomePage
          homepageData={homepageData}
          handleToggle={handleToggle}
          handleCardClick={handleCardClick}
          userGroup={userGroup}
          muted={muted}
          setMuted={setMuted}
          setCardPlaying={setCardPlaying}
          cardPlaying={cardPlaying}
          setModal={setModal}
          onQuery={onQuery}
          remixedCards={remixedCards}
          handleToggleAll={handleToggleAll}
        />
      </Box>
    );
  }

  if (!cards.length) {
    return (
      <>
        <NoCardsFound />
        <Box>
          <HomePage
            homepageData={homepageData}
            handleToggle={handleToggle}
            handleCardClick={handleCardClick}
            userGroup={userGroup}
            muted={muted}
            setMuted={setMuted}
            setCardPlaying={setCardPlaying}
            cardPlaying={cardPlaying}
            setModal={setModal}
            onQuery={onQuery}
            remixedCards={remixedCards}
          />
        </Box>
      </>
    );
  }

  return (
    <>
      <GenerateCardGridFromData
        cards={cards}
        handleToggle={handleToggle}
        handleCardClick={handleCardClick}
        userGroup={userGroup}
        muted={muted}
        setMuted={setMuted}
        setCardPlaying={setCardPlaying}
        cardPlaying={cardPlaying}
        setModal={setModal}
        onQuery={onQuery}
        remixedCards={remixedCards}
        category={category}
      />
    </>
  );
}

const GenerateCardGridFromData = ({
  cards,
  handleToggle,
  handleCardClick,
  setModal,
  onQuery,
  outsideControl,
}: any) => {
  const { allstarUser } = useUser();
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();

  return (
    <>
      <CardGridStyle pb={4} width="100%">
        {cards.map(function (card: any, index: any) {
          const isMusicCard = card.facet?.type?.includes("Music");
          const userGroup = allstarUser?.user?.userGroup?.type;
          const displayRarity = showSubscriptionOverhaul
            ? isMusicCard
              ? null
              : "ESSENTIALS"
            : undefined;

          // With overhaul: Music is free, non-Music requires ESSENTIALS+
          const cardHasAccess = showSubscriptionOverhaul
            ? isMusicCard || userGroup !== "FREE"
            : !!hasScope(allstarUser?.user, card.scopeUse);

          return (
            <Tilt
              tiltMaxAngleX={6}
              tiltMaxAngleY={6}
              perspective={900}
              transitionSpeed={1500}
              scale={1.01}
              gyroscope={false}
              key={card._id}
            >
              <StyledBox rarity={card.ui.rarity}>
                <CreatorCard
                  card={card}
                  handleCardClick={handleCardClick}
                  setModal={setModal}
                  isModal={false}
                  handleModalClose={() => {}}
                />
                <Controls
                  isActive={card.isActive}
                  handleCardClick={handleCardClick}
                  card={card}
                  handleToggle={handleToggle}
                  onQuery={onQuery}
                  tooltipContent={<TieredCard rarity={card.ui.rarity} />}
                  setModal={setModal}
                  hideToolTip={allstarUser.loggedIn && cardHasAccess}
                  outsideControl={outsideControl}
                  hasScope={cardHasAccess}
                  displayRarity={displayRarity}
                />
              </StyledBox>
            </Tilt>
          );
        })}
      </CardGridStyle>
    </>
  );
};

const getboxShadowColor = (rarity: any) => {
  if (rarity === 500) {
    return `#3D444F 0 1px 4px, #3D444F 0 2px 10px, #3D444F 0 10px 20px,
  #3D444F 0 18px 40px, 5px 5px 15px 5px rgba(0, 0, 0, 0);`;
  }

  //legendary, todo
  if (rarity === 400) {
    return `#2E3749 0 1px 4px, #2E3749 0 2px 10px, #2E3749 0 10px 20px,
  #2E3749 0 18px 40px, 5px 5px 15px 5px rgba(0, 0, 0, 0);`;
  }

  //rare, todo
  if (rarity === 300) {
    return `#383342 0 1px 4px, #383342 0 2px 10px, #383342 0 10px 20px,
  #383342 0 18px 40px, 5px 5px 15px 5px rgba(0, 0, 0, 0);`;
  }

  //premium, todo
  if (rarity === 200) {
    return `#283940 0 1px 4px, #283940 0 2px 10px, #283940 0 10px 20px,
  #283940 0 18px 40px, 5px 5px 15px 5px rgba(0, 0, 0, 0);`;
  }

  //standard, todo
  if (rarity === 100) {
    return `#0f1519 0 1px 4px, #0f1519 0 2px 10px, #1b242c 0 10px 20px,
  #161f25 0 18px 40px, 5px 5px 15px 5px rgba(0, 0, 0, 0);`;
  }

  return `#0f1519 0 1px 4px, #0f1519 0 2px 10px, #1b242c 0 10px 20px,
  #161f25 0 18px 40px, 5px 5px 15px 5px rgba(0, 0, 0, 0);`;
};

// @phillip, these are the colors for the border & hover

// Vitality: `rgba(255,255,0,.4);`; hover `rgba(255,255,0,.8);`;
// Ence: `rgba(255,155,73,.4);`; hover `rgba(255,155,73,.8);`;
// GamerLegion: `rgba(45,61,106,.4);`; hover `rgba(45,61,106,.8);`;
// Cloud9: `rgba(34,148,210,.4);`; hover `rgba(34,148,210,.8);`;
// Navi: `rgba(255,242,38,.4);`; hover `rgba(255,242,38,.8);`;
// BetBoom: `rgba(238,61,63,.4);`; hover `rgba(238,61,63,.8);`;
// Grayhound: `rgba(163,170,178,.4);`; hover `rgba(163,170,178,.8);`;
// Complexity: `rgba(255,255,255,.4);`; hover `rgba(255,255,255,.8);`;
// Apeks: `rgba(255,100,0,.4);`; hover `rgba(255,100,0,.8);`;
// FazeClan: `rgba(237,34,36,.4);`; hover `rgba(237,34,36,.8);`;
// Monte: `rgba(57,200,248,.4);`; hover `rgba(57,200,248,.8);`;
// Mouz: `rgba(255,0,0,.3);`; hover `rgba(255,0,0,.7);`;
// Vertex ESC: `rgba(255,255,255,.4);`; hover `rgba(255,255,255,.8);`;
// Lynn Vision: `rgba(178,127,29,.4);`; hover `rgba(178,127,29,.8);`;

const StyledBox: any = styled(Box)`
  border-radius: 12px;
  border: 2px solid
    ${(props: any) => {
      //@Chris, changes border colors
      if (props.rarity === 500) {
        return props.theme.colors.mythicAlpha40;
      }

      //legendary, todo
      if (props.rarity === 400) {
        return props.theme.colors.legendaryAlpha40;
      }

      //rare, todo
      if (props.rarity === 300) {
        return props.theme.colors.rareAlpha40;
      }

      //premium, todo
      if (props.rarity === 200) {
        return props.theme.colors.premiumAlpha40;
      }

      //standard, todo
      if (props.rarity === 100) {
        return props.theme.colors.darkerNavy;
      }

      return "rgba(255,255,0,.4);";
    }};
  &:hover {
    cursor: pointer;
    -webkit-box-shadow: ${(props: any) => {
      return getboxShadowColor(props.rarity);
    }};
    box-shadow: ${(props: any) => {
      return getboxShadowColor(props.rarity);
    }}
    background: ${theme.colors.midnightAlpha50};
    border: 2px solid
    ${(props: any) => {
      //@Chris, changes border colors
      if (props.rarity === 500) {
        return props.theme.colors.mythicAlpha80;
      }

      //legendary, todo
      if (props.rarity === 400) {
        return props.theme.colors.legendaryAlpha80;
      }

      //rare, todo
      if (props.rarity === 300) {
        return props.theme.colors.rareAlpha80;
      }

      //premium, todo
      if (props.rarity === 200) {
        return props.theme.colors.premiumAlpha80;
      }

      //standard, todo
      if (props.rarity === 100) {
        return props.theme.colors.nedLight;
      }

      return "rgba(255,255,0,.8);";
    }};
  }
`;

export const CardGridStyle = styled(Flex)<any>`
  display: grid;
  grid-gap: 24px;
  /* Increased minmax values for larger card sizes */
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  list-style: none;
  @media (min-width: 1600px) {
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  }
  @media (max-width: 715px) {
    grid-template-columns: repeat(auto-fill, minmax(220px, 2fr));
  }
  @media (max-width: 410px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 2fr));
  }
  @media (max-width: 400px) {
    grid-gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(160px, 2fr));
  }
`;

const Empty = styled(Flex)`
  margin: auto;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 160px;
  left: 1%;
  @media (max-width: 1000px) {
    left: 20%;
  }
  @media (max-width: 600px) {
    left: 0%;
  }
`;

const StyledLarge3 = styled(Large3)`
  font-weight: 600;
`;

const StyledText = styled(Flex)`
  flex-wrap: wrap;
  max-width: 290px;
  text-align: center;
`;

const StyledTitle3 = styled(Title3)`
  font-weight: 600;
`;

const StyledSubheader = styled(Subheader)`
  font-weight: 500;
  color: ${theme.colors.chalk};
  text-align: center;
`;

// Compact Card Grid for TimeRemap song selection
// Shows only image/video preview with title, no tags or controls

interface ICompactCardGridProps {
  cards: any[];
  selectedCardId?: string | null;
  onCardSelect: (card: any) => void;
  hoveredCardId?: string | null;
  onCardHover?: (cardId: string | null) => void;
}

export const CompactCardGrid = ({
  cards,
  selectedCardId,
  onCardSelect,
  hoveredCardId,
  onCardHover,
}: ICompactCardGridProps) => {
  const [isMuted, setIsMuted] = useState(false);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});

  const handleMuteToggle = (e: React.MouseEvent, cardId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMuted(!isMuted);
    const video = videoRefs.current[cardId];
    if (video) {
      video.muted = !isMuted;
    }
  };

  return (
    <CompactCardGridStyle>
      {cards.map((card: any) => {
        const isHovered = hoveredCardId === card._id;
        const hasVideo = card.ui.hoverEffectSrc;

        return (
          <CompactCard
            key={card._id}
            onClick={() => onCardSelect(card)}
            onMouseEnter={() => onCardHover?.(card._id)}
            onMouseLeave={() => onCardHover?.(null)}
            $isSelected={selectedCardId === card._id}
          >
            <CompactCardPreview>
              {isHovered && hasVideo ? (
                <>
                  <CompactCardVideo
                    ref={(el) => {
                      videoRefs.current[card._id] = el;
                    }}
                    src={`${process.env.REACT_APP_S3_STATIC}/static/${card.ui.hoverEffectSrc}`}
                    autoPlay
                    loop
                    muted={isMuted}
                    playsInline
                    onLoadStart={(
                      e: React.SyntheticEvent<HTMLVideoElement>,
                    ) => {
                      e.currentTarget.volume = 0.3;
                    }}
                  />
                  <MuteButton onClick={(e) => handleMuteToggle(e, card._id)}>
                    {isMuted ? <StyledMutedIcon /> : <StyledUnMutedIcon />}
                  </MuteButton>
                </>
              ) : (
                <CompactCardImage
                  src={`${process.env.REACT_APP_S3_STATIC}/static/${card.ui.image}`}
                  alt={card.ui.title}
                />
              )}
            </CompactCardPreview>
            <CompactCardTitle>{card.ui.title}</CompactCardTitle>
          </CompactCard>
        );
      })}
    </CompactCardGridStyle>
  );
};

const CompactCardGridStyle = styled(Box)`
  display: grid;
  flex: 1;
  gap: 12px;
  grid-template-columns: repeat(3, 1fr);
  overflow-y: auto;
  padding: 0 24px 16px;

  ::-webkit-scrollbar {
    width: 25px;
  }
  ::-webkit-scrollbar-corner {
    background: ${({ theme }) => theme.colors.ned};
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.ned};
    background-clip: content-box;
    border: 5px solid transparent;
    border-radius: 999px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }

  @media (max-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CompactCard = styled(Box)<{ $isSelected: boolean }>`
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.chalkAlpha10 : "transparent"};
  border: 2px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.envy : "transparent"};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.envy : theme.colors.chalkAlpha40};
  }
`;

const CompactCardPreview = styled(Box)`
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.colors.grey825};
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const CompactCardImage = styled.img`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const CompactCardVideo = styled.video`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const CompactCardTitle = styled(Box)`
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.midnightAlpha80} 0%,
    ${({ theme }) => theme.colors.midnight} 100%
  );
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  padding: 8px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MuteButton = styled(Box)`
  align-items: center;
  background: ${({ theme }) => theme.colors.midnightAlpha50};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  height: 28px;
  justify-content: center;
  left: 8px;
  position: absolute;
  top: 8px;
  width: 28px;
  z-index: 10;

  &:hover {
    background: ${({ theme }) => theme.colors.midnightAlpha80};
  }
`;

const StyledMutedIcon = styled(MutedIcon)`
  color: ${({ theme }) => theme.colors.ice};
  height: 18px;
  width: 18px;
`;

const StyledUnMutedIcon = styled(UnMutedIcon)`
  color: ${({ theme }) => theme.colors.ice};
  height: 18px;
  width: 18px;
`;
